import React from 'react';
import {RouteComponentProps} from 'react-router';
import {PaymentMethod} from 'grpc-bob/enum_pb';

import PaymentLayout from '../../layouts/PaymentLayout';
import Description from '../../components/Description';
import TableDetail from '../../components/TableDetail';
import DetailItem from '../../components/DetailItem';
import {ButtonProps} from '../../components/Button';
import Promotion from '../../components/Promotion';
import Paper from '../../components/Paper';

import {useEffectOnce} from 'react-use';
import {useTranslation} from 'react-i18next';
import {useDispatchCtx} from '../../context';

import SubscriptionService from '../../services/subscription';
import {AppState, setMessage, setPaymentOrderId} from '../../state-management/app';
import getKnowNative, {MIXPANEL} from '../../externals/mobile-communication';

import {
  appendQuery,
  getActualMoney,
  getMsgFromErr,
  isPromotionValid,
  ClientStorage,
  toVND,
} from '../../utils';
import {termOfCondition} from '../../configs';
import {PROMOTION_STATUS} from '../../constants/enum';
import {PaymentMethodKeyReverse} from '../../constants/map';

import styles from './PaymentDetail.module.css';


interface PaymentDetailProps extends AppState, RouteComponentProps {
  onConfirm: (rawPromotionCode: string) => void
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}


const PaymentDetail = (props: PaymentDetailProps) => {
  const {t} = useTranslation();
  const dispatch = useDispatchCtx();
  const {
    user,
    chosenPackage,
    promotion,
    history,
    location,
    token
  } = props;

  useEffectOnce(() => {
    if (!chosenPackage) return history.push(`/package?${location.search}`);

    //change currentStep of mobile
    getKnowNative('details');

    //mixpanel
    getKnowNative('mixpanel', undefined, {
      type: MIXPANEL.PAYMENT_FLOW_ENTER,
      planId: chosenPackage.planId,
      packageId: chosenPackage.id,
      packageName: chosenPackage.name,
      totalAmount: getActualMoney(chosenPackage),
      promotionCodeApplied: isPromotionValid(promotion),
      promotionCode: isPromotionValid(promotion) ? promotion.text : ''
    });
  });

  const onChoosePaymentMethod = async () => {
    if (!chosenPackage) {
      return dispatch(setMessage('pleaseChoosePackage'));
    }

    const isPromoValid = isPromotionValid(promotion);
    //mixpanel
    getKnowNative('mixpanel', undefined, {
      type: MIXPANEL.PAYMENT_DETAIL_CONFIRMED,
      planId: chosenPackage.planId,
      packageId: chosenPackage.id,
      packageName: chosenPackage.name,
      totalAmount: getActualMoney(chosenPackage),
      promotionCodeApplied: isPromoValid,
      promotionCode: isPromoValid ? promotion.text : ''
    });

    try {
      const resp = await SubscriptionService.getSubscriptionOrder(
        chosenPackage.id,
        user!.id,
        isPromotionValid(promotion) ? promotion.text : undefined,
        token as string
      );

      if (!resp.successful) {
        return dispatch(setMessage('cannotCreateOrder'));
      }

      dispatch(setPaymentOrderId(resp.orderId));

      ClientStorage.orderProcessing = {
        orderId: resp.orderId,
        paymentUrl: resp.paymentUrl,
        paymentMethod: PaymentMethodKeyReverse[PaymentMethod.PAYMENT_METHOD_NONE]
      };

      history.push(`/method?${appendQuery(location.search, 'Ref', String(resp.orderId))}`);

    } catch (e) {
      const commonErr = getMsgFromErr(e);

      dispatch(setMessage(commonErr || e.message));
    }
  };

  const buttonProps: ButtonProps = { //too much dependencies, no need useMemo
    disabled: promotion.status === PROMOTION_STATUS.INVALID,
    children: t('selectPaymentMethod'),
    onClick: onChoosePaymentMethod,
  };

  return (
    <PaymentLayout buttonProps={buttonProps}>
      {
        chosenPackage &&
        <Paper className={styles.root}>
          <div>
            <h3 className="text is-title">{t('draftInvoice')}</h3>
            <Description className="m-b-1" title={t('packageName')}>
              {chosenPackage.name}
            </Description>
            {/*<Description className="m-b-1" title={t('serviceExpiryDate')}>*/}
            {/*  {t('toDate', getDateProperty(chosenPackage?.packageItemsList))}*/}
            {/*</Description>*/}
            <Description className="m-b-1" title={t('promotion')}>
              <Promotion
                promotion={props.promotion}
                onChange={props.onChange}
                onConfirm={props.onConfirm}
                onClick={()=>getKnowNative('inputFocus')}
              />
            </Description>
            <Description className="m-b-1" title={t('paymentDetails')}>
              <TableDetail>
                <div>
                  <DetailItem
                    title={t('originalAmount')}
                    value={toVND(chosenPackage.price)}
                  />
                  {
                    chosenPackage.carriedOverAmount ?
                      <DetailItem
                        valueClass={styles.discount}
                        title={t('carryOverFee')}
                        value={toVND(chosenPackage.carriedOverAmount || 0)}
                      /> : null
                  }
                  {
                    isPromotionValid(promotion) &&
                    chosenPackage.isPromotionApplied &&
                    <DetailItem
                      valueClass={styles.discount}
                      title={t('discount')}
                      value={toVND(chosenPackage.promotionDiscountValue || 0)}
                    />
                  }
                </div>
                <hr className={styles.horizontal}/>
                <div>
                  <DetailItem
                    valueClass={styles.total}
                    title={t('total')}
                    value={toVND(getActualMoney(chosenPackage))}
                  />
                </div>
              </TableDetail>
            </Description>
          </div>
          <div className={styles.termContainer}>
            <a
              className={styles.term}
              href={termOfCondition[user!.country]}
              target="_self"
              onClick={() => getKnowNative('term')}
              rel="noopener noreferrer"
            >
              {t('termAndCondition')}
            </a>
          </div>
        </Paper>
      }
    </PaymentLayout>
  );
};

export default PaymentDetail;
